import {Arrays, int, isDefined, Subscription, Terminable, UUID} from "@opendaw/lib-std"
import {ClipNotification} from "@opendaw/studio-adapters"
import {Project} from "@opendaw/studio-core"
import {DrumVariant} from "@/generate/DrumGenerator"
import {Rng} from "@/generate/Rng"
import {DrumMachine} from "./DrumMachine"
import {BassLine} from "./BassLine"

export namespace Arrangement {
    export const Cycles: int = 64

    // One variant per four-bar cycle. Cycle 1 brings the first fill (bar 7-8), after that a break is rare and
    // never follows another, and the last cycle of every eight is the build.
    export const plan = (seed: int): ReadonlyArray<DrumVariant> => {
        const rng = new Rng(seed + 4711)
        const plan: Array<DrumVariant> = []
        Arrays.create(cycle => cycle, Cycles).forEach(cycle => plan.push(choose(cycle, plan[cycle - 1], rng)))
        return plan
    }

    const choose = (cycle: int, previous: DrumVariant, rng: Rng): DrumVariant => {
        if (cycle === 0) {return "pattern"}
        if (cycle === 1) {return "fill"}
        if (previous !== "pattern") {return "pattern"}
        if (cycle % 8 === 7) {return "build"}
        const roll = rng.next()
        return roll < 0.2 ? "fill" : roll < 0.3 ? "drop" : "pattern"
    }
}

// Follows the plan on the engine's clip clock: every started clip schedules the next one, which the engine
// hands over at the four-bar boundary. The 303 moves to its next variation when the pattern returns after a break.
export class Arranger implements Terminable {
    readonly #project: Project
    readonly #drums: DrumMachine
    readonly #bass: BassLine
    readonly #plan: ReadonlyArray<DrumVariant>
    readonly #kinds: Map<string, DrumVariant>

    #cycle: int = -1
    #playing: UUID.Bytes = UUID.Lowest
    #subscription: Subscription = Terminable.Empty

    constructor(project: Project, drums: DrumMachine, bass: BassLine, plan: ReadonlyArray<DrumVariant>) {
        this.#project = project
        this.#drums = drums
        this.#bass = bass
        this.#plan = plan
        this.#kinds = new Map(drums.clips.map(({kind, clip}) => [UUID.toString(clip.address.uuid), kind]))
    }

    // Once the worklet is ready and before play: the first clip then waits at position zero.
    start(): void {
        this.#subscription = this.#project.engine
            .subscribeClipNotification(notification => this.#onNotification(notification))
        this.#schedule(this.#plan[0])
    }

    terminate(): void {this.#subscription.terminate()}

    #onNotification(notification: ClipNotification): void {
        if (notification.type !== "sequencing") {return}
        notification.changes.started.forEach(uuid => {
            const kind = this.#kinds.get(UUID.toString(uuid))
            if (!isDefined(kind)) {return}
            this.#playing = uuid
            this.#cycle++
            const previous = this.#plan[(this.#cycle + this.#plan.length - 1) % this.#plan.length]
            if (this.#cycle > 0 && kind === "pattern" && previous !== "pattern") {this.#bass.advance()}
            this.#schedule(this.#plan[(this.#cycle + 1) % this.#plan.length])
        })
    }

    #schedule(kind: DrumVariant): void {
        this.#project.engine.scheduleClipPlay([this.#drums.clipOf(kind, this.#playing).clip.address.uuid])
    }
}
